/**
 * Confirmation when the user marks a task complete from the conversation footer.
 *
 * Copy differs when the composer has a message (send + complete) vs empty (complete only).
 *
 * @module taskCompleteConfirmModal
 */
import { useEffect, useState } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../../../enums/modalId";

/**
 * Outline warning triangle for the complete-confirm modal (design token sizing).
 *
 * @returns Decorative SVG; not interactive.
 */
function TaskCompleteWarningGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="38"
      height="38"
      viewBox="0 0 38 38"
      fill="none"
      className="h-[2.33331rem] w-[2.33331rem] shrink-0"
      aria-hidden
    >
      <path
        d="M18.6685 14.0004V20.2226M18.6685 26.4448H18.684M16.5145 6.05415L3.72024 28.1533C3.01059 29.3791 2.65576 29.992 2.7082 30.495C2.75395 30.9337 2.98381 31.3324 3.34059 31.5918C3.74963 31.8893 4.45782 31.8893 5.87419 31.8893H31.4627C32.8791 31.8893 33.5873 31.8893 33.9963 31.5918C34.3531 31.3324 34.583 30.9337 34.6287 30.495C34.6812 29.992 34.3263 29.3791 33.6167 28.1533L20.8224 6.05415C20.1153 4.83278 19.7617 4.2221 19.3005 4.01699C18.8981 3.83809 18.4388 3.83809 18.0364 4.01699C17.5752 4.2221 17.2216 4.83278 16.5145 6.05415Z"
        stroke="#1F1F1F"
        strokeWidth="2.07407"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Which confirmation copy to show in {@link TaskCompleteConfirmModal}.
 */
export type TaskCompleteConfirmVariant = "sendAndComplete" | "markCompleteOnly";

/**
 * Props for {@link TaskCompleteConfirmModal}.
 */
interface TaskCompleteConfirmModalProps {
  onClose: () => void;
  variant: TaskCompleteConfirmVariant;
  /** Invoked when the user confirms; reject to keep the modal open. */
  onConfirm?: () => Promise<void>;
}

const VARIANT_COPY: Record<
  TaskCompleteConfirmVariant,
  { title: string; message: string }
> = {
  sendAndComplete: {
    title: "Send and Mark as Complete",
    message: "Are you sure you want to send and complete this task?",
  },
  markCompleteOnly: {
    title: "Mark as Complete",
    message: "Are you sure you want to complete this task?",
  },
};

/**
 * Oasis modal with centered warning, body copy, and No / Yes footer actions.
 *
 * @param props - Variant, close handler, and optional confirm callback.
 * @returns Mounted only while the confirmation should be visible.
 */
const TaskCompleteConfirmModal = ({
  onClose,
  variant,
  onConfirm,
}: TaskCompleteConfirmModalProps) => {
  const { title, message } = VARIANT_COPY[variant];
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    openModal(ModalId.TASK_CONVERSATION_COMPLETE_CONFIRM_MODAL);
  }, []);

  const dismiss = () => {
    if (isConfirming) {
      return;
    }
    closeModal(ModalId.TASK_CONVERSATION_COMPLETE_CONFIRM_MODAL, onClose);
  };

  /**
   * Runs confirm work, then closes only after it resolves successfully.
   */
  const handleConfirm = async () => {
    if (!onConfirm) {
      dismiss();
      return;
    }

    setIsConfirming(true);
    try {
      await onConfirm();
      closeModal(ModalId.TASK_CONVERSATION_COMPLETE_CONFIRM_MODAL, onClose);
    } catch {
      // Parent surfaces toast; keep modal open for retry.
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal
      dialogId={ModalId.TASK_CONVERSATION_COMPLETE_CONFIRM_MODAL}
      title={title}
      saveButtonText={isConfirming ? "Confirming..." : "Yes, Confirm"}
      cancelText="No, Cancel"
      onSave={handleConfirm}
      onCancel={dismiss}
      onClose={dismiss}
      disableSave={isConfirming}
    >
      <div className="flex flex-col items-center gap-5 px-1 pb-2 pt-4">
        <div className="flex h-14 w-14 items-center justify-center gap-3 rounded-[2.18rem] bg-primaryGray-16">
          <TaskCompleteWarningGlyph />
        </div>
        <p className="max-w-[20rem] text-center text-[1rem] font-normal leading-[1.5rem] text-[#000]">
          {message}
        </p>
      </div>
    </Modal>
  );
};

export default TaskCompleteConfirmModal;
